import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  /** Title text for the empty state */
  title: string;
  /** Optional description text */
  description?: string;
  /** Optional icon element displayed above the title */
  icon?: ReactNode;
  /** Optional call-to-action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

function SparkleDecoration() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {/* Top-right sparkle */}
      <svg
        className="absolute top-2 right-4 opacity-40"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z"
          fill="var(--color-primary)"
        />
      </svg>
      {/* Bottom-left sparkle */}
      <svg
        className="absolute bottom-4 left-6 opacity-30"
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z"
          fill="var(--color-secondary)"
        />
      </svg>
      {/* Top-left small sparkle */}
      <svg
        className="absolute top-6 left-10 opacity-25"
        width="8"
        height="8"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z"
          fill="var(--color-accent)"
        />
      </svg>
      {/* Bottom-right sparkle */}
      <svg
        className="absolute bottom-6 right-8 opacity-35"
        width="10"
        height="10"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z"
          fill="var(--color-primary-light)"
        />
      </svg>
    </div>
  );
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ title, description, icon, action, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={[
          'relative flex flex-col items-center justify-center px-6 py-12 text-center',
          'rounded-md',
          className,
        ].join(' ')}
        {...props}
      >
        <SparkleDecoration />

        {icon && (
          <div className="mb-4 text-text-secondary opacity-70">{icon}</div>
        )}

        <h3 className="text-heading-3 font-semibold text-text-heading mb-2">
          {title}
        </h3>

        {description && (
          <p className="text-body text-text-secondary max-w-xs mb-6">
            {description}
          </p>
        )}

        {action && (
          <button
            onClick={action.onClick}
            className={[
              'inline-flex items-center justify-center font-semibold',
              'px-5 py-2.5 text-body',
              'btn-primary text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
            ].join(' ')}
          >
            {action.label}
          </button>
        )}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';
